import LicensingService from 'src/services/licensing';
import types from '../mutation-types';

const state = {
  // Customer Portal State
  licenseSummary: {
    organization: '',
    total_licenses: 0,
    assigned_count: 0,
    available_count: 0,
  },
  devices: [],
  deviceFilter: 'all', // 'all' | 'assigned' | 'unassigned' | 'exempt'
  licenseEvents: [],

  // Admin Portal State
  adminSelectedOrg: null,
  adminOrgLicenses: {
    total_licenses: 0,
    assigned_count: 0,
    available_count: 0,
  },
  adminOrgDevices: [],
  adminDeviceFilter: 'all', // 'all' | 'assigned' | 'unassigned' | 'exempt'
  adminLicenseEvents: [],
};

const getters = {
  licenseSummary: (state) => state.licenseSummary,
  licensingDevices: (state) => state.devices,
  deviceFilter: (state) => state.deviceFilter,
  licenseEvents: (state) => state.licenseEvents,

  // Admin getters
  adminSelectedOrg: (state) => state.adminSelectedOrg,
  adminOrgLicenses: (state) => state.adminOrgLicenses,
  adminOrgDevices: (state) => state.adminOrgDevices,
  adminDeviceFilter: (state) => state.adminDeviceFilter,
  adminLicenseEvents: (state) => state.adminLicenseEvents,
  filteredAdminOrgDevices: (state) => {
    if (state.adminDeviceFilter === 'all') return state.adminOrgDevices;
    return state.adminOrgDevices.filter(
      (d) => d.license_status === state.adminDeviceFilter
    );
  },

  // Computed getters
  filteredLicensingDevices: (state) => {
    if (state.deviceFilter === 'all') return state.devices;
    return state.devices.filter((d) => d.license_status === state.deviceFilter);
  },
  licenseUsagePercent: (state) => {
    if (state.licenseSummary.total_licenses === 0) return 0;
    return Math.round(
      (state.licenseSummary.assigned_count /
        state.licenseSummary.total_licenses) *
        100
    );
  },
};

const actions = {
  // ==================== Customer Portal Actions ====================

  async fetchLicenseSummary({ commit }) {
    const [error, data] = await LicensingService.getLicenseSummary();
    if (error) {
      console.error('Failed to fetch license summary:', error);
      return [error, null];
    }
    commit(types.SET_LICENSE_SUMMARY, data);
    return [null, data];
  },

  async fetchLicensingDevices({ commit, state }) {
    const queryParams =
      state.deviceFilter !== 'all' ? { status: state.deviceFilter } : {};
    const [error, data] = await LicensingService.getDevices(queryParams);
    if (error) {
      console.error('Failed to fetch devices:', error);
      return [error, null];
    }
    commit(types.SET_LICENSING_DEVICES, data);
    return [null, data];
  },

  setDeviceFilter({ commit, dispatch }, filter) {
    commit(types.SET_DEVICE_FILTER, filter);
    dispatch('fetchLicensingDevices');
  },

  async assignLicenseToDevice({ dispatch }, deviceId) {
    const [error, data] = await LicensingService.assignLicense(deviceId);
    if (error) {
      return [error, null];
    }
    // Refresh data after assignment
    dispatch('fetchLicenseSummary');
    dispatch('fetchLicensingDevices');
    return [null, data];
  },

  async revokeLicenseFromDevice({ dispatch }, deviceId) {
    const [error, data] = await LicensingService.revokeLicense(deviceId);
    if (error) {
      return [error, null];
    }
    // Refresh data after revocation
    dispatch('fetchLicenseSummary');
    dispatch('fetchLicensingDevices');
    return [null, data];
  },

  async fetchLicenseEvents({ commit }, queryParams = {}) {
    const [error, data] = await LicensingService.getLicenseEvents(queryParams);
    if (error) {
      console.error('Failed to fetch license events:', error);
      return [error, null];
    }
    commit(types.SET_LICENSE_EVENTS, data);
    return [null, data];
  },

  // ==================== Admin Portal Actions ====================

  setAdminSelectedOrg({ commit }, org) {
    commit(types.SET_ADMIN_SELECTED_ORG, org);
  },

  async fetchAdminOrgLicenses({ commit, state }) {
    if (!state.adminSelectedOrg) return [{ message: 'No org selected' }, null];
    const [error, data] = await LicensingService.getOrgLicenses(
      state.adminSelectedOrg
    );
    if (error) {
      console.error('Failed to fetch org licenses:', error);
      return [error, null];
    }
    commit(types.SET_ADMIN_ORG_LICENSES, data);
    return [null, data];
  },

  async updateAdminOrgLicenses({ dispatch, state }, totalLicenses) {
    if (!state.adminSelectedOrg) return [{ message: 'No org selected' }, null];
    const [error, data] = await LicensingService.updateOrgLicenses(
      state.adminSelectedOrg,
      { total_licenses: totalLicenses }
    );
    if (error) {
      return [error, null];
    }
    dispatch('fetchAdminOrgLicenses');
    return [null, data];
  },

  async fetchAdminOrgDevices({ commit, state }) {
    if (!state.adminSelectedOrg) return [{ message: 'No org selected' }, null];
    const queryParams =
      state.adminDeviceFilter !== 'all'
        ? { status: state.adminDeviceFilter }
        : {};
    const [error, data] = await LicensingService.getAdminOrgDevices(
      state.adminSelectedOrg,
      queryParams
    );
    if (error) {
      console.error('Failed to fetch org devices:', error);
      return [error, null];
    }
    commit(types.SET_ADMIN_ORG_DEVICES, data);
    return [null, data];
  },

  setAdminDeviceFilter({ commit, dispatch }, filter) {
    commit(types.SET_ADMIN_DEVICE_FILTER, filter);
    dispatch('fetchAdminOrgDevices');
  },

  async adminAssignLicenseToDevice({ dispatch, state }, deviceId) {
    if (!state.adminSelectedOrg) return [{ message: 'No org selected' }, null];
    const [error, data] = await LicensingService.adminAssignLicense(
      state.adminSelectedOrg,
      deviceId
    );
    if (error) {
      return [error, null];
    }
    // Refresh data after assignment
    dispatch('fetchAdminOrgLicenses');
    dispatch('fetchAdminOrgDevices');
    return [null, data];
  },

  async adminRevokeLicenseFromDevice({ dispatch, state }, deviceId) {
    if (!state.adminSelectedOrg) return [{ message: 'No org selected' }, null];
    const [error, data] = await LicensingService.adminRevokeLicense(
      state.adminSelectedOrg,
      deviceId
    );
    if (error) {
      return [error, null];
    }
    // Refresh data after revocation
    dispatch('fetchAdminOrgLicenses');
    dispatch('fetchAdminOrgDevices');
    return [null, data];
  },

  async fetchAdminLicenseEvents({ commit, state }, queryParams = {}) {
    if (!state.adminSelectedOrg) return [{ message: 'No org selected' }, null];
    const [error, data] = await LicensingService.getAdminLicenseEvents(
      state.adminSelectedOrg,
      queryParams
    );
    if (error) {
      console.error('Failed to fetch license events:', error);
      return [error, null];
    }
    commit(types.SET_ADMIN_LICENSE_EVENTS, data);
    return [null, data];
  },

  resetLicensingState({ commit }) {
    commit(types.SET_LICENSE_SUMMARY, {
      organization: '',
      total_licenses: 0,
      assigned_count: 0,
      available_count: 0,
    });
    commit(types.SET_LICENSING_DEVICES, []);
    commit(types.SET_DEVICE_FILTER, 'all');
    commit(types.SET_LICENSE_EVENTS, []);
    commit(types.SET_ADMIN_SELECTED_ORG, null);
    commit(types.SET_ADMIN_ORG_LICENSES, {
      total_licenses: 0,
      assigned_count: 0,
      available_count: 0,
    });
    commit(types.SET_ADMIN_ORG_DEVICES, []);
    commit(types.SET_ADMIN_DEVICE_FILTER, 'all');
    commit(types.SET_ADMIN_LICENSE_EVENTS, []);
  },
};

const mutations = {
  [types.SET_LICENSE_SUMMARY](state, payload) {
    state.licenseSummary = payload;
  },
  [types.SET_LICENSING_DEVICES](state, payload) {
    state.devices = payload;
  },
  [types.SET_DEVICE_FILTER](state, payload) {
    state.deviceFilter = payload;
  },
  [types.SET_LICENSE_EVENTS](state, payload) {
    state.licenseEvents = payload;
  },
  [types.SET_ADMIN_SELECTED_ORG](state, payload) {
    state.adminSelectedOrg = payload;
  },
  [types.SET_ADMIN_ORG_LICENSES](state, payload) {
    state.adminOrgLicenses = payload;
  },
  [types.SET_ADMIN_ORG_DEVICES](state, payload) {
    state.adminOrgDevices = payload;
  },
  [types.SET_ADMIN_DEVICE_FILTER](state, payload) {
    state.adminDeviceFilter = payload;
  },
  [types.SET_ADMIN_LICENSE_EVENTS](state, payload) {
    state.adminLicenseEvents = payload;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
